import React from "react";
import Anonymous from "./Anonymous";
import icon_users from "./resources/icons/users.svg";
import icon_map_pin from "./resources/icons/map-pin.svg";
import icon_archive from "./resources/icons/archive.svg";
import icon_report from "./resources/icons/report.svg";
import icon_education from "./resources/icons/education.svg";
import icon_institution from "./resources/icons/institution.svg";
import icon_tools from "./resources/icons/tools.svg";
import icon_flask from "./resources/icons/flask.svg";
import icon_lock_file from "./resources/icons/lock-file.svg";
import icon_open_file from "./resources/icons/document-open.svg";
import icon_community from "./resources/icons/community.svg";
import icon_document from "./resources/icons/document.svg";
import icon_scales from "./resources/icons/scales.svg";
import icon_user_plus from "./resources/icons/user-plus.svg";
import SETTINGS from "../globals";
import AppSections from "../utils/AppSections";
import { useNavigate } from "react-router-dom";
import YouTube from "react-youtube";

function Welcome() {
  // reference to the navigator
  const navigate = useNavigate();

  const video_opts = {
    height: "500",
    width: "890",
    playerVars: {
      // https://developers.google.com/youtube/player_parameters
      autoplay: 0,
      rel: 0,
    },
  };

  return (
    <Anonymous>
      <div className="wellcome-container">
        <section className="section-1">
          <div className="section-1-left">
            <p className="eyebrow">
              Technology to protect Europe’s cultural legacy
            </p>
            <h1 className="big-title">
              Collaborative Digital Monitoring for Cultural Heritage
            </h1>
            <p className="lead">
              Upload, document, analyse, annotate, and exchange 3D scans,
              sensor-based data, documents, and condition reports for cultural
              heritage assets. Work together across disciplines to understand,
              preserve, and share our shared heritage.
            </p>
          </div>
          <div className="mt-24">
            <YouTube videoId="PQSV5EU7oDk" opts={video_opts} id="video" />;
          </div>
        </section>

        <section className="section-2" id="tools">
          <h2 className="medium-title">
            One workspace for heritage data, analysis, and collaboration
          </h2>
          <div className="feature-grid">
            <article>
              <img src={icon_archive} alt="" />
              <h3>Document Heritage Assets</h3>
              <p>
                Upload 3D scans, images, documents, and sensor data in one place
                with rich metadata and context.
              </p>
            </article>
            <article>
              <img src={icon_map_pin} alt="" />
              <h3>Map Evidence on 3D Surfaces</h3>
              <p>
                Visualise and analyse scientific data directly on 3D models with
                advanced mapping tools.
              </p>
            </article>
            <article>
              <img src={icon_users} alt="" />
              <h3>Annotate and Collaborate</h3>
              <p>
                Add annotations, share insights, and collaborate with your team
                and the wider community.
              </p>
            </article>
            <article>
              <img src={icon_report} alt="" />
              <h3>Create Condition Reports</h3>
              <p>
                Generate professional reports and export findings to support
                conservation decisions and planning.
              </p>
            </article>
          </div>
        </section>

        <section className="section-2" id="records">
          <h2 className="medium-title">
            Open metadata. Licensed data. Clear responsibilities.
          </h2>
          <div className="license-grid">
            <article className="license-card metadata-card">
              <img src={icon_open_file} alt="" />
              <div>
                <h3>Metadata</h3>
                <ul>
                  <li>Openly accessible to everyone</li>
                  <li>Searchable and discoverable</li>
                  <li>Available anonymously</li>
                  <li>Supports transparency and discovery</li>
                </ul>
              </div>
            </article>
            <article className="license-card data-card">
              <img src={icon_lock_file} alt="" />
              <div>
                <h3>Data</h3>
                <ul>
                  <li>Released under a selected license</li>
                  <li>License defined by the contributor</li>
                  <li>Reuse subject to stated conditions</li>
                  <li>Supports ethical and responsible use</li>
                </ul>
              </div>
            </article>
          </div>
        </section>

        <section className="section-2">
          <h2 className="medium-title">
            Designed for the cultural heritage community
          </h2>
          <div className="audience-grid">
            <article>
              <img src={icon_tools} alt="" />
              <span>Conservators and Restorers</span>
            </article>
            <article>
              <img src={icon_flask} alt="" />
              <span>Heritage Scientists</span>
            </article>
            <article>
              <img src={icon_education} alt="" />
              <span>Researchers and Educators</span>
            </article>
            <article>
              <img src={icon_institution} alt="" />
              <span>Institutions and Site Managers</span>
            </article>
            <article>
              <img src={icon_community} alt="" />
              <span>Communities and Citizen Science Initiatives</span>
            </article>
          </div>
        </section>

        <section className="responsibility section-pad compact" id="guide">
          <div className="column-sketch" aria-hidden="true"></div>
          <div className="responsibility-copy">
            <h2 className="medium-title">
              Responsible sharing for sensitive
              <br />
              heritage data
            </h2>
            <p>
              Contributors are responsible for ensuring they have the rights to
              share data and for selecting an appropriate license. Users are
              responsible for complying with the license conditions and any
              applicable legal requirements.
            </p>
            <div className="trust-links">
              <a
                onClick={() =>
                  navigate(
                    SETTINGS.get_path({
                      section: AppSections.TERMS_AND_CONDITIONS,
                    }),
                  )
                }
              >
                <img src={icon_document} alt="" />
                Terms and Conditions
              </a>
              <a
                onClick={() =>
                  navigate(
                    SETTINGS.get_path({
                      section: AppSections.COMMUNITY_RULES,
                    }),
                  )
                }
              >
                <img src={icon_users} alt="" />
                Community Guide
              </a>
              <a
                onClick={() =>
                  navigate(
                    SETTINGS.get_path({
                      section: AppSections.LICENSE_AGREEMENT,
                    }),
                  )
                }
              >
                <img src={icon_scales} alt="" />
                Licensing Notice
              </a>
            </div>
          </div>
        </section>

        <section className="cta" id="upload">
          <div className="cta-icon">
            <img src={icon_institution} alt="" />
          </div>
          <div>
            <h2>Start documenting, analysing, and sharing heritage data</h2>
            <p style={{ fontSize: "18px" }}>
              Join a growing community dedicated to understanding and preserving
              our shared heritage.
            </p>
          </div>
          <div className="cta-actions">
            <a
              className="button button-light"
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.SIGNUP,
                  }),
                )
              }
            >
              <img src={icon_user_plus} alt="" />
              Create an Account
            </a>
          </div>
        </section>
      </div>
    </Anonymous>
  );
}

export default Welcome;
